import { Typography } from '@mui/material';
import * as Sentry from '@sentry/gatsby';
import React from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import Alert from '@components/Alert';

import { FormattedError } from '@models/error';

const messages = defineMessages({
  noAccess: {
    id: 'errorMessage.noAccess',
    defaultMessage: 'No Access',
  },
  noAccessSub: {
    id: 'errorMessage.inaccessible',
    defaultMessage: 'This user has not given you access to these records.',
  },
  notFound: {
    id: 'errorMessage.notFound',
    defaultMessage: 'Not Found',
  },
  notFoundSub: {
    id: 'errorMessage.notFoundDescription',
    defaultMessage: 'Unable to find the records you were looking for',
  },
  serverError: {
    id: 'errorMessage.internalServerError',
    defaultMessage: 'Internal Server Error',
  },
  issue: {
    id: 'errorMessage.issue',
    defaultMessage: 'Something went wrong',
  },
});

export default function ErrorBoundaryFallback(props: FallbackProps) {
  const { formatMessage } = useIntl();
  const [error, setError] = React.useState<FormattedError>();

  React.useEffect(() => {
    const { error } = props;
    if (error instanceof FormattedError) {
      switch (error.code) {
        case 'FORBIDDEN':
        case '403':
          setError(
            new FormattedError(
              formatMessage(messages.noAccess),
              formatMessage(messages.noAccessSub)
            )
          );
          break;
        default:
          setError(new FormattedError(error.code, error.message));
          break;
      }
    } else {
      Sentry.captureException(error);
      setError(
        new FormattedError(
          formatMessage(messages.serverError),
          formatMessage(messages.issue)
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.error]);

  return (
    <Alert severity="error" variant="standard" title={error?.code}>
      <Typography variant="subtitle1">{error?.message}</Typography>
    </Alert>
  );
}
